import { useMemo } from 'react';
import { useTranslations } from '@lang';

interface NotificationParams {
  phoneNumber?: string;
  entityName?: string;
  spaEntityLabel?: string;
  pluralEntityLabel?: string;
  formattedDate?: string;
  onCtaClick?: () => void;
}

export const useBookingFormLabels = (language: string, notification?: NotificationParams) => {
  const { t } = useTranslations();

  const counter = useMemo(
    () => ({
      decrease: t('spa.booking.counter.decrease'),
      increase: t('spa.booking.counter.increase'),
    }),
    [t]
  );

  const reservation = useMemo(
    () => ({
      title: t('spa.booking.reservation.title'),
      subtitle: t('spa.booking.reservation.subtitle'),
      guestLabel: t('spa.booking.reservation.guestLabel'),
    }),
    [t]
  );

  const calendar = useMemo(
    () => ({
      previousWeek: t('spa.booking.calendar.previousWeek'),
      nextWeek: t('spa.booking.calendar.nextWeek'),
      openCalendar: t('spa.booking.calendar.openCalendar'),
    }),
    [t]
  );

  const timeslotChips = useMemo(
    () => ({
      title: t('spa.booking.timeslots.title'),
      previousPage: t('spa.booking.timeslots.previous'),
      nextPage: t('spa.booking.timeslots.next'),
      locale: language,
    }),
    [t, language]
  );

  const guestDetails = useMemo(
    () => ({
      subtitle: t('spa.booking.guestDetails.subtitle'),
      dividerText: t('spa.booking.guestDetails.divider'),
      titlePlaceholder: t('spa.booking.guestDetails.selectPlaceholder'),
      titleLabel: t('common.title'),
      firstNameLabel: t('common.firstName'),
      lastNameLabel: t('common.lastName'),
      genderLabel: t('common.gender'),
      guestTitleFormatter: (n: number) => t('spa.guestCounter', { counter: n }),
    }),
    [t]
  );

  const preferences = useMemo(
    () => ({
      otherButtonLabel: t('spa.booking.preferences.otherButton'),
    }),
    [t]
  );

  const stickyFooterTotalLabel = t('basket.total');

  const telephoneOnly = useMemo(() => {
    if (!notification?.phoneNumber || !notification?.spaEntityLabel) return undefined;
    return {
      title: t('spa.telephoneOnly', { spaEntity: notification.spaEntityLabel }),
      subtitle: t('treatment.callUsOn', { phoneNumber: notification.phoneNumber }),
    };
  }, [t, notification?.phoneNumber, notification?.spaEntityLabel]);

  const unavailable = useMemo(() => {
    if (
      !notification?.entityName ||
      !notification?.formattedDate ||
      !notification?.pluralEntityLabel
    )
      return undefined;
    return {
      title: t('common.unavailable'),
      subtitle: t('spa.package.unavailableDescription', {
        title: notification.entityName,
        date: notification.formattedDate,
      }),
      ctaLabel: t('common.backToModuleRoot', { entity: notification.pluralEntityLabel }),
      onCtaClick: notification.onCtaClick,
    };
  }, [
    t,
    notification?.entityName,
    notification?.formattedDate,
    notification?.pluralEntityLabel,
    notification?.onCtaClick,
  ]);

  return {
    counter,
    reservation,
    calendar,
    timeslotChips,
    guestDetails,
    preferences,
    stickyFooterTotalLabel,
    telephoneOnly,
    unavailable,
  };
};
